/**
 * Zudoku 0.77 portal configuration.
 *
 * One build documents one version
 * -------------------------------
 * Every published version (develop, and one per release YYMM_N) is built
 * once and served from its own folder next to the others
 * (specifications/BriteCore/<version>/ on api.britecore.com). A build never
 * carries the history: the cost of publishing stays the cost of one version,
 * however many releases exist. Moving between versions is a full page load
 * driven by the shared versions.json -- see pages/versions.mdx.
 *
 * Why we route the home to /home (not /)
 * --------------------------------------
 * Zudoku 0.77 mishydrates the root route. Whether the page comes from a
 * pages/index.mdx or from a navigation link with `to: "/"`, the browser
 * console shows React error #418 ("text content mismatch") on first load.
 * Reproduced locally with the bytewise-equivalent upstream example --
 * adding ANY content for "/" trips the bug.
 *
 * Workaround: don't put any Zudoku content at "/". The home lives at
 * /home (pages/home.mdx), and a tiny static index.html (written by
 * scripts/postbuild-root-redirect.mjs after the build) does a HTML
 * meta-refresh from "/" to "/home". No React on the root means no
 * hydration there, so no #418.
 *
 * Once Zudoku ships a release that fixes the root-route hydration, we
 * can collapse home.mdx back to index.mdx and drop the postbuild step.
 */
import type { ZudokuConfig } from "zudoku";
import { apiCatalogPlugin } from "zudoku/plugins/api-catalog";
import eventsSidebar from "./events.sidebar.generated.json";
import eventsManifest from "./events.manifest.json";
import buildInfo from "./build-info.generated.json";

// The sidebar JSON is generated by scripts/build-events-sidebar.mjs at
// prebuild time. It groups every event MDX under its topic so the left rail
// of the events catalog mirrors the directory layout.
const eventsSidebarItems = eventsSidebar as unknown[];

// Build the events catalog plugin items. Zudoku's apiCatalogPlugin gives us
// a card grid with a tag filter; each card opens the per-event MDX page and
// carries its topic root as the category, so a visitor can filter the
// catalog by domain (claims, payments, ...).
type EventManifestEntry = {
  class_name: string;
  topic: string;
  topic_root: string;
  description: string;
  schema_id: string;
  version: string;
  // "events/<topic_root>/<event>.mdx", written by generate_event_docs.py
  // for the page it renders. The route is derived from it rather than
  // reconstructed from the topic, so a card can never point at a page that
  // was not generated.
  mdx_path: string;
};
const events = (eventsManifest as { events?: EventManifestEntry[] }).events ?? [];

const capitalise = (word: string) => word.charAt(0).toUpperCase() + word.slice(1);
const eventTopicRoots = Array.from(new Set(events.map((e) => e.topic_root))).sort();

// Zudoku's CatalogPage builds the chip filter from `category.label` (NOT
// from `category.tags`). To get one chip per domain (claims, payments, ...)
// instead of a single "Domain" chip with a dropdown, we promote each
// topic_root to be its own category label and stuff the same value as the
// only tag.
//
// (apiCatalogPlugin's other strings -- "Search APIs…" placeholder, "Browse
// every API across the platform" header copy -- are hardcoded in the
// component and not configurable. We accept that for now.)
const eventCatalogItems = events.map((e) => ({
  // docs.files strips the leading "events/" off the route, and the route
  // drops the .mdx extension.
  path: `/${e.mdx_path.replace(/^events\//, "").replace(/\.mdx$/, "")}`,
  label: e.class_name,
  description: e.description,
  categories: [{ label: capitalise(e.topic_root), tags: [capitalise(e.topic_root)] }],
  version: e.version,
}));

// Prefix every absolute URL the build emits (assets, routes, the favicon)
// with the folder the build is served from. GitHub Pages owns the root of
// its domain and builds with an empty base path; the S3 deployment serves
// each version from its own folder of api.britecore.com.
//
// The value is frozen into build-info.generated.json at prebuild, because
// this module is re-evaluated in the browser where the build environment is
// gone and `process.env` reads as undefined.
const basePath = buildInfo.basePath || undefined;

// A quoted CSS string. Preview labels are branch names, so quotes and
// backslashes must not end the string early.
const cssString = (value: string) => `"${value.replace(/\\/g, "\\\\").replace(/"/g, '\\"')}"`;

// Paths below stay root-relative on purpose. Zudoku prefixes them itself
// once basePath reaches the client, so adding the prefix here as well
// produces a doubled path.
const config: ZudokuConfig = {
  ...(basePath ? { basePath } : {}),
  // Apply the "Dark Matter" theme from tweakcn.com. Zudoku resolves the
  // registry URL at build time and bakes the resulting CSS variables into
  // the bundle, so there is no runtime fetch.
  theme: {
    registryUrl: "https://tweakcn.com/r/themes/darkmatter.json",
    // The version being read, as a badge beside the "Versions" tab. Zudoku's
    // navigation `badge` option only renders in the sidebar; the header
    // drops it, so the badge is drawn here instead. The label is baked per
    // build, which is right: a build only ever documents its own version.
    //
    // It must be ::before, not ::after: Zudoku draws the active tab's
    // underline with the link's ::after, and sharing it turns the badge into
    // that absolutely positioned bar. The link is a flex row, so order: 1
    // still places the badge after the text, spaced by the link's own gap.
    customCss: `
      header a[href$="/versions"]::before {
        content: ${cssString(buildInfo.version.label)};
        order: 1;
        padding: 0.05rem 0.45rem;
        border: 1px solid currentColor;
        border-radius: 9999px;
        font-size: 0.7em;
        font-weight: 500;
        line-height: 1.4;
        opacity: 0.75;
        vertical-align: middle;
      }
    `,
  },
  site: {
    title: "BriteCore Developer Portal",
    logo: {
      src: { light: "/britecore-logo.png", dark: "/britecore-logo.png" },
      alt: "BriteCore",
      width: "180px",
      // Click on the logo lands directly on the overview page. Without
      // this, Zudoku defaults to "/" which our postbuild redirect bounces
      // to /home -- the user sees a flash of the redirect HTML before
      // landing. Pointing the logo at /home skips the round-trip.
      href: "/home",
    },
    footer: {
      copyright: `© ${new Date().getFullYear()} BriteCore. All rights reserved.`,
    },
  },
  metadata: {
    title: "BriteCore Developer Portal",
    description:
      "REST API reference and event catalog for integrating with the BriteCore platform.",
    favicon: "/favicon.svg",
  },
  // Surface the built-in "Copy / Open in Claude / Open in ChatGPT" dropdown
  // on every documentation page. For event MDX it appears next to the page
  // table of contents; for the OpenAPI reference it shows up next to the
  // "Download schema" button. Both presets pass the current page URL to the
  // AI assistant so the model can fetch the rendered docs.
  aiAssistants: ["claude", "chatgpt"],
  navigation: [
    { type: "link", label: "Overview", to: "/home" },
    { type: "link", label: "API", to: "/api" },
    // Single "Events" tab. We use a `category` (not a `link`) so the
    // sidebar items below it appear when the user is anywhere under the
    // events tree. The category's `link` is the destination of the header
    // click.
    {
      type: "category",
      label: "Events",
      link: "events", // pages/events.mdx -- a thin react-router redirect to /events-catalog
      collapsible: true,
      collapsed: false,
      // The printable PDF is deliberately NOT linked from here. Navigation
      // links render through react-router and Zudoku 0.77 drops `target` on
      // both sidebar and top-nav items, so a nav entry pointing at a static
      // asset is always intercepted and lands on the 404 page. The download
      // is offered from MDX instead (pages/home.mdx and the generated events
      // catalog pages), where a raw anchor survives the render.
      items: eventsSidebarItems as never,
    },
    // Leads to the list of all published versions. The version being read
    // is drawn beside it as a badge by theme.customCss above.
    { type: "link", label: "Versions", to: "/versions" },
  ],
  apis: {
    type: "file",
    input: "./public/api/britecore.openapi.json",
    path: "api",
    options: {
      // Surface the "Download / Copy / Open in Claude / Open in ChatGPT"
      // button next to each API page's title. The AI options are wired
      // through the top-level `aiAssistants` setting; without
      // `schemaDownload.enabled: true` the button itself doesn't render.
      schemaDownload: {
        enabled: true,
      },
    },
  },
  // Site-wide search. Pagefind indexes the static HTML output at build
  // time, runs entirely in the browser (no backend), and covers MDX pages,
  // the events catalog and the OpenAPI reference uniformly. The index lives
  // in dist/pagefind/ and ships with the site.
  search: {
    type: "pagefind",
  },
  docs: {
    // pages/ contains the hand-written portal pages. events/ contains the
    // per-event MDX generated by scripts/generate_event_docs.py. Routes are
    // inferred from the path (events/policies/policy_active.mdx ->
    // /policies/policy_active).
    files: ["/pages/**/*.mdx", "/events/**/*.mdx"],
    defaultOptions: {
      // The "Last modified" footer is prerendered in UTC on the build runner
      // and re-rendered in the reader's timezone: whenever the build time
      // falls on a different calendar day for the reader, the text differs
      // and React fails hydration (#418). It also says the wrong thing --
      // it is the time CI built the page, not the time its content changed.
      showLastModified: false,
    },
  },
  plugins: [
    // Render a card-grid catalog of every event. Each card links to the
    // corresponding per-event MDX page; the category filter bar at the top
    // groups them by domain (claims, payments, policies, ...).
    apiCatalogPlugin({
      // Mounted at /events-catalog so it doesn't collide with
      // pages/events.mdx (which the Events nav category links to). The MDX
      // page handles the routing/sidebar binding; the plugin renders the
      // actual card grid and is the destination of the MDX page's redirect.
      path: "events-catalog",
      label: "Events catalog",
      categories: eventTopicRoots.map((root) => ({
        label: capitalise(root),
        tags: [capitalise(root)],
      })),
      items: eventCatalogItems,
    }),
  ],
};

export default config;
